import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  BarChart3,
  Calendar,
  ClipboardList,
  Users,
  Scale,
  Briefcase,
  FileText,
  ChevronRight,
} from "lucide-react";
import { api } from "@/App";

const iconMap = {
  Calendar,
  ClipboardList,
  Users,
  Scale,
  Briefcase,
  FileText,
  BarChart3,
};

export default function DataGuides() {
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGuides = async () => {
      try {
        const res = await api.get("/data-guides");
        setGuides(res.data);
      } catch (error) {
        // Error fetching data guides
      } finally {
        setLoading(false);
      }
    };
    fetchGuides();
  }, []);

  return (
    <div data-testid="data-guides-page">
      {/* Header */}
      <section className="bg-slate-900 text-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <p className="label-uppercase text-orange-400 mb-4">Data & Dashboards</p>
            <h1 className="text-4xl sm:text-5xl font-bold mb-6" data-testid="data-guides-title">
              Data Guides
            </h1>
            <p className="text-lg text-slate-300">
              Not live dashboards—guides that tell you what data to pull, how to interpret it, 
              and what actions to take. Grounded in the "coherence" lens for PPS leadership decisions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-12 bg-blue-50 border-b border-blue-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h2 className="text-xl font-bold text-slate-900 mb-4">
              Six Critical Data Domains for PPS Leaders
            </h2>
            <p className="text-slate-600">
              These are the data areas every PPS leader should monitor regularly. 
              Each domain connects to multiple framework pillars and provides 
              leading indicators of system health.
            </p>
          </div>
        </div>
      </section>

      {/* Guides Grid */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full" />
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6" data-testid="guides-grid">
              {guides.map((guide, index) => {
                const Icon = iconMap[guide.icon] || BarChart3;
                return (
                  <motion.div
                    key={guide.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Link
                      to={`/data/${guide.slug}`}
                      className="block bg-white border border-slate-200 hover:border-slate-300 hover:shadow-lg transition-all duration-300 h-full"
                      style={{ borderLeftColor: guide.color, borderLeftWidth: "4px" }}
                      data-testid={`guide-card-${guide.slug}`}
                    >
                      <div className="p-6 lg:p-8">
                        <div className="flex items-start gap-4">
                          <div 
                            className="w-12 h-12 rounded-sm flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: `${guide.color}15` }}
                          >
                            <Icon 
                              className="w-6 h-6" 
                              style={{ color: guide.color }}
                              strokeWidth={1.5}
                            />
                          </div>
                          <div className="flex-1">
                            <p className="text-xs font-mono text-slate-400 uppercase mb-1">
                              {guide.domain}
                            </p>
                            <h3 className="text-xl font-bold text-slate-900 mb-2">
                              {guide.title}
                            </h3>
                            
                            {/* What to Pull Preview */}
                            <div className="mb-4">
                              <p className="text-sm font-medium text-slate-700 mb-2">
                                What to pull:
                              </p>
                              <ul className="text-sm text-slate-600 space-y-1">
                                {guide.what_to_pull?.slice(0, 3).map((item, i) => (
                                  <li key={i} className="flex items-start gap-2">
                                    <span className="text-slate-400">•</span>
                                    {item}
                                  </li>
                                ))}
                                {guide.what_to_pull?.length > 3 && (
                                  <li className="text-slate-400 text-xs">
                                    +{guide.what_to_pull.length - 3} more items
                                  </li>
                                )}
                              </ul>
                            </div>

                            <span className="inline-flex items-center text-sm font-medium text-blue-600">
                              View Full Guide
                              <ChevronRight className="w-4 h-4 ml-1" />
                            </span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          )}

          {!loading && guides.length === 0 && (
            <div className="text-center py-12 bg-slate-50 rounded-sm">
              <BarChart3 className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-slate-900 mb-2">
                Data Guides Coming Soon
              </h2>
              <p className="text-slate-600">
                We're currently building out our data guide library. Check back soon!
              </p>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl lg:text-3xl font-bold mb-4">
            Need Help Interpreting Your Data?
          </h2>
          <p className="text-blue-100 mb-6">
            Sometimes you need a second set of eyes. Let's review your data together 
            and develop actionable next steps for your district.
          </p>
          <Link to="/about#contact">
            <button className="h-11 px-6 bg-white text-blue-600 hover:bg-blue-50 font-medium rounded-md transition-colors" data-testid="data-consultation-btn">
              Schedule a Data Consultation
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
}
